import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { InboxesApi } from 'entities/Inbox'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { SmartCallApi } from 'entities/SmartCall/api/SmartCallApi'
import { Inbox } from 'entities/Inbox/model/Inbox.ts'
import { type IResponseBusinessHours } from 'entities/Users/api/types.ts'
import { MembersStore } from 'pages/settings/pages/inboxes/pages/id/store/membersStore'
import { InboxSettingsStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/store/inboxSettingsStore'
import { type InboxesViewStore } from 'pages/settings/pages/inboxes/pages/inboxesView/store/inboxesViewStore'
import { NumbersStore } from './numbersStore'

export class InboxLayerStore {
  private _numbersStore: NumbersStore
  private _membersStore: MembersStore
  private _team: IResponseInbox | null = null
  private _inboxSettingsStore: InboxSettingsStore
  private _isCallFlowEnabled = false

  constructor(
    private _inboxesViewStore: InboxesViewStore,
    private readonly _entities: IEntitiesStore
  ) {
    this._membersStore = new MembersStore(this, this._entities)
    this._numbersStore = new NumbersStore(this, this._entities)
    this._inboxSettingsStore = new InboxSettingsStore(this, this._entities)

    makeAutoObservable(this)
  }

  get numbersStore() {
    return this._numbersStore
  }

  get membersStore() {
    return this._membersStore
  }

  get inboxesViewStore() {
    return this._inboxesViewStore
  }

  get entities() {
    return this._entities
  }

  get currentTeam() {
    return this._team
  }

  get inboxId() {
    return this._team?.id || 0
  }

  get inboxSettingsStore() {
    return this._inboxSettingsStore
  }

  get isAircallOnlyTeam() {
    return this._team?.numbers.every((number) => number.is_aircall)
  }

  get isCallFlowEnabled() {
    return this._isCallFlowEnabled
  }

  setIsCallFlowEnabled = (value: boolean) => {
    this._isCallFlowEnabled = value
  }

  updateCurrentTeamName = (name: string) => {
    if (!this._team) return
    this._team.name = name
  }
  updateCurrentTeamBusinessHours = (business_hours: IResponseBusinessHours) => {
    if (!this._team) return
    this._team.business_hours = business_hours
    const inbox = this.entities.inboxesStore.getItem(this._team.id)
    if (inbox instanceof Inbox) {
      inbox.updateBusinessHours(business_hours)
    }
  }

  init = async (inboxId: number) => {
    const team = await this.loadTeam(inboxId)

    if (team) {
      await Promise.all([
        this._membersStore.init(inboxId),
        this.numbersStore.init(inboxId),
        this._inboxSettingsStore.init(team),
        this._loadCallFlowStatus(inboxId),
      ])
    }
  }

  loadTeam = async (inboxId: number) => {
    try {
      const response = await InboxesApi.getTeamById(inboxId)
      const team = response.data

      this._entities.inboxesStore.addItem(team)

      runInAction(() => {
        this._team = team
      })

      return team
    } catch (error) {
      console.error(error)
    }
  }

  private _loadCallFlowStatus = async (inboxId: number) => {
    try {
      const res = await SmartCallApi.getInboxWorkflow(inboxId)
      const payload = res.data.data
      runInAction(() => {
        this._isCallFlowEnabled = !!payload?.active && (payload.workflow?.enabled ?? false)
      })
    } catch (error) {
      logger.error(error)
    }
  }

  dispose = () => {
    this._membersStore.dispose()
    this._numbersStore.dispose()
    this._inboxSettingsStore.dispose()
  }
}
